import { payerTypeConstants } from "constants/payer";
import { getAbnProcedureLines, getAbnSelectionProcedures } from "helpers/abn";
import { interpolateString } from "helpers/interpolate";
import { centsToDollars, parsePrice } from "helpers/price";
import { IAbnNotice } from "types/AbnNotice/AbnNotice";

import { getAbnReason } from "../../helpers";
import {
  abnSelectionCommercialOptions,
  abnSelectionMedicareOptions,
  medicareNote,
} from "./constants";

/**
 * The ABN options the patient chooses from, with the estimated amount written into their text —
 * Medicare (CMS-R-131) wording for Original Medicare, the commercial wording otherwise. Checkout
 * shows and signs these; the unsigned ABN download prints the same ones.
 */
export function getAbnSelectionOptions(
  isMedicare: boolean,
  totalAmountInCents: number
) {
  const baseOptions = isMedicare
    ? abnSelectionMedicareOptions
    : abnSelectionCommercialOptions;

  const formattedAmount = parsePrice(centsToDollars(totalAmountInCents));

  // boldPrefix must be interpolated the same way as label — it has to stay a literal prefix of
  // label or the PDF drops the bold styling (CLE-2659).
  return baseOptions.map((option) => ({
    ...option,
    label: interpolateString(option.label, { amount: formattedAmount }),
    ...("boldPrefix" in option && {
      boldPrefix: interpolateString((option as any).boldPrefix, {
        amount: formattedAmount,
      }),
    }),
  }));
}

/**
 * An ABN for an order that requires one but has not been signed at checkout, shaped like a signed
 * notice so the ABN PDF renders it: the same codes, lines, options, reason and Medicare note
 * checkout shows — but no option selected and no signature or date, so it can be printed and
 * signed by hand.
 *
 * Returns null when the order needs no ABN.
 */
export function buildUnsignedAbnNotice(order: any): IAbnNotice | null {
  const procedures = getAbnSelectionProcedures(order);
  if (!procedures?.length) return null;

  const isMedicare = order?.payer?.type === payerTypeConstants.MEDICARE;
  const totalAmountInCents = procedures.reduce(
    (total, procedure) => total + (procedure.amount || 0),
    0
  );

  return {
    payerId: order?.payer?.id,
    payer: order?.payer,
    documentSnapshot: {
      availableOptions: getAbnSelectionOptions(isMedicare, totalAmountInCents),
      reason: getAbnReason(isMedicare),
      ...(isMedicare && { note: medicareNote }),
      procedures: procedures.map((procedure) => ({
        ...procedure,
        lines: getAbnProcedureLines(order, procedure.cptCode),
      })),
    },
  } as unknown as IAbnNotice;
}
